/**
 * Startup secret-loading (main thread only).
 *
 * Fetches the OpenAI key and the AWS credentials from the Access API (access_api.ts) and installs
 * them into the engine's runtime setters. This runs once, behind the startup password gate — the
 * secrets are NOT bundled, so until this succeeds DynamoDB stays local-only and AI features are off.
 *
 * The worker never calls this: it never touches DynamoDB or the AWS keys, and OpenAI runs on the
 * main thread.
 */

import {
  AWS_SECRET_KEY,
  OPENAI_SECRET_KEY,
  fetch_secret,
  set_access_password,
} from "./access_api";
import { set_aws_credentials } from "./dynamo";
import { set_openai_api_key } from "./openai_service";

let _loaded = false;

/** True once both secrets have been fetched and installed this session. */
export function secrets_loaded(): boolean {
  return _loaded;
}

/**
 * Fetch + install all runtime secrets using `password`. On success the password is persisted to
 * sessionStorage and `secrets_loaded()` becomes true. Throws `AccessUnauthorizedError` on a wrong
 * password (nothing is installed/persisted) and a generic error on any other failure.
 */
export async function load_secrets(password: string): Promise<void> {
  // btr_oai → a plain string (the OpenAI key); btr_aws → { api, secret }.
  const [oai, aws] = await Promise.all([
    fetch_secret(OPENAI_SECRET_KEY, password),
    fetch_secret(AWS_SECRET_KEY, password),
  ]);

  if (typeof oai !== "string" || !oai.trim()) {
    throw new Error("Access API returned an unexpected OpenAI secret shape");
  }
  if (!aws || typeof aws !== "object" || typeof aws.api !== "string" || typeof aws.secret !== "string") {
    throw new Error("Access API returned an unexpected AWS secret shape");
  }

  set_openai_api_key(oai);
  set_aws_credentials({ accessKeyId: aws.api, secretAccessKey: aws.secret });
  set_access_password(password);
  _loaded = true;
}
